import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { AgentLeadCard, SelectOptions } from '@interfaces';
import { OrderService } from '@services';
import { LeadColumnStatus } from '@types';
import { PalleteColor } from 'nanaykuna-ui-kit/loader';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'unicon-identity-field',
  templateUrl: './identity-field.component.html',
  styleUrl: './identity-field.component.scss',
})
export class IdentityFieldComponent {
  @Input() leadNames?: string;
  @Input() leadFirstSurname?: string;
  @Input() leadSecondSurname?: string;
  @Input() leadStatusName?: LeadColumnStatus;
  @Input() roleName!: string;
  @Input() isRecurring!: boolean;
  @Input() isPinned!: boolean;
  @Input() hasHighClosingProbability!: boolean;
  @Input() isActivated!: boolean;
  @Input() user?: AgentLeadCard;
  @Input() orderId!: string;
  @Input() showDetail!: boolean;
  @Input() showBtnClose: boolean = false;
  @Input() options: SelectOptions[] = [
    { id: 1, name: 'assigned', description: 'Asignado' },
    { id: 2, name: 'processing', description: 'En Atención' },
    { id: 3, name: 'quoted', description: 'Cotizado' },
    { id: 4, name: 'scheduled', description: 'En Programación' },
    { id: 5, name: 'paid', description: 'Pagado' },
    { id: 6, name: 'outForDelivery', description: 'En despacho' },
    { id: 7, name: 'afterSale', description: 'Post Venta' },
  ];
  protected form!: FormGroup;
  protected formSelect!: FormGroup;
  protected showEditNames: boolean = false;
  protected edit: boolean = false;
  protected hovered: boolean = false;
  @ViewChild('documentFieldElement') formField!: ElementRef<HTMLFormElement>;
  @Output() result: EventEmitter<Record<string, string>> = new EventEmitter<
    Record<string, string>
  >();
  @Output() onShowDetail = new EventEmitter<void>();
  @ViewChild('pinAlertButton') pinAlertButton!: ElementRef<HTMLButtonElement>;
  @ViewChild('frameAlertButton')
  frameAlertButton!: ElementRef<HTMLButtonElement>;
  protected error: string = '';
  protected iconShowDetail: string = 'minus';

  constructor(
    private formBuilder: FormBuilder,
    private orderService: OrderService,
  ) {
    this.setForm();
  }

  ngOnInit(): void {
    this.setInitialValues();
  }

  ngAfterViewInit(): void {
    this.setEditListeners();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['leadNames']) {
      this.getPrincipalText();
    }

    if (changes['leadFirstSurname']) {
      this.getPrincipalText();
    }

    if (changes['leadSecondSurname']) {
      this.getPrincipalText();
    }
  }

  private setEditListeners(): void {
    const formComponent: HTMLFormElement = this.formField.nativeElement;
    formComponent?.addEventListener('mouseenter', () => {
      this.hovered = true;
    });
    formComponent?.addEventListener('mouseleave', () => {
      this.hovered = false;
    });
  }

  private setInitialValues(): void {
    const selectedOption: SelectOptions[] = this.options.filter(item => {
      return item.description === this.leadStatusName;
    });
    if (this.leadNames) {
      this.form.controls['leadNames'].setValue(this.leadNames ?? '');
    }
    if (this.leadFirstSurname) {
      this.form.controls['leadFirstSurname'].setValue(
        this.leadFirstSurname ?? '',
      );
    }

    if (this.leadSecondSurname) {
      this.form.controls['leadSecondSurname'].setValue(
        this.leadSecondSurname ?? '',
      );
    }

    if (this.leadStatusName) {
      this.formSelect.controls['selectedColumn'].setValue(
        selectedOption[0]?.id ?? null,
      );
    }
  }

  private setForm(): void {
    this.form = this.formBuilder.group({
      leadNames: new FormControl<string>(
        { disabled: true, value: '' },
        Validators.compose([Validators.required]),
      ),
      leadFirstSurname: new FormControl<string>(
        { disabled: true, value: '' },
        Validators.compose([Validators.required]),
      ),
      leadSecondSurname: new FormControl<string>(
        { disabled: true, value: '' },
        Validators.compose([Validators.required]),
      ),
    });

    this.formSelect = this.formBuilder.group({
      selectedColumn: new FormControl<number | null>({
        disabled: false,
        value: null,
      }),
    });
  }

  protected getBadgeText() {
    if (this.user) {
      const fullname: string =
        this.user?.firstName +
        (this.user?.lastName ? ' ' + this.user?.lastName : '');
      if (fullname) {
        const slices: string[] = fullname.split(' ');
        return slices[0][0] + (slices[1] ? slices[1][0] : '');
      }
      return '';
    }
    return '';
  }

  protected getPrincipalText(): string {
    let fullname = '';
    if (this.leadNames) {
      fullname = `${this.leadNames}`;
    }

    if (this.leadFirstSurname) {
      fullname = `${fullname} ${this.leadFirstSurname}`;
    }

    if (this.leadSecondSurname) {
      fullname = `${fullname} ${this.leadSecondSurname}`;
    }

    return fullname;
  }

  protected onEditNames() {
    this.form.enable();
    this.showEditNames = true;
    this.edit = true;
  }

  protected getIconColor(): PalleteColor {
    if (this.form.errors) {
      return 'critical-300';
    }

    if (this.edit) {
      return 'neutrals-900';
    }

    if (this.hovered) {
      return 'neutrals-900';
    }

    return 'neutrals-500';
  }

  protected onSendFullname() {
    const errors =
      this.form.controls['leadNames'].value === '' ||
      this.form.controls['leadFirstSurname'].value === '' ||
      this.form.controls['leadSecondSurname'].value === '';
    if (!errors) {
      this.result.emit(this.form.value);
      this.showEditNames = false;
    }
  }

  protected onFrame(): void {
    if (this.orderId) {
      this.orderService.togglePFrameOrder(this.orderId).subscribe({
        next: () => {
          this.isRecurring = !this.isRecurring;
        },
        error: (error: HttpErrorResponse) => {
          this.error = error.error.detail;
          const btn: HTMLButtonElement = this.frameAlertButton.nativeElement;
          btn.click();
        },
      });
    }
  }

  protected onPin(): void {
    if (this.orderId) {
      this.orderService.togglePinOrder(this.orderId).subscribe({
        next: () => {
          this.isPinned = !this.isPinned;
        },
        error: (error: HttpErrorResponse) => {
          this.error = error.error.detail;
          const btn: HTMLButtonElement = this.pinAlertButton.nativeElement;
          btn.click();
        },
      });
    }
  }

  protected onSelectedOption(newOption: number): void {
    this.orderService.patchStatusOrder(this.orderId, newOption).subscribe();
  }

  protected onClosingProbability(): void {
    if (this.orderId) {
      this.orderService.updateClosingProbability(this.orderId).subscribe({
        next: () => {
          this.hasHighClosingProbability = !this.hasHighClosingProbability;
        },
      });
    }
  }

  protected onShowIconDetail() {
    this.onShowDetail.emit();
    if (this.iconShowDetail === 'minus') {
      this.iconShowDetail = 'plus';
    } else {
      this.iconShowDetail = 'minus';
    }
  }
}
